
import { Check, AlertTriangle, X } from "lucide-react";
import { FlightStatus } from "@/types/flight";
import { cn } from "@/lib/utils";

interface FlightStatusBadgeProps {
  status: FlightStatus;
  className?: string;
  showIcon?: boolean;
  pulsing?: boolean;
  showActualStatus?: boolean; // For admin/org views to see actual "On Time" status
}

export function FlightStatusBadge({ 
  status, 
  className,
  showIcon = true,
  pulsing = false,
  showActualStatus = false
}: FlightStatusBadgeProps) {
  // For public views, display "On Time" as "-"
  const displayStatus = !showActualStatus && status === "On Time" ? "-" : status;
  const statusConfig: Record<FlightStatus, { bgColor: string; textColor: string; borderColor: string; icon: any }> = {
    "Scheduled": {
      bgColor: "bg-slate-100",
      textColor: "text-slate-800",
      borderColor: "border-slate-200",
      icon: Check
    },
    "Check-in Open": {
      bgColor: "bg-blue-100",
      textColor: "text-blue-800",
      borderColor: "border-blue-200",
      icon: Check
    },
    "Check-in Closed": {
      bgColor: "bg-purple-100",
      textColor: "text-purple-800",
      borderColor: "border-purple-200",
      icon: Check
    },
    "Boarding Soon": {
      bgColor: "bg-indigo-100",
      textColor: "text-indigo-800",
      borderColor: "border-indigo-200",
      icon: Check
    },
    "Boarding": {
      bgColor: "bg-cyan-100",
      textColor: "text-cyan-800",
      borderColor: "border-cyan-200",
      icon: Check
    },
    "Gate Closed": {
      bgColor: "bg-orange-100",
      textColor: "text-orange-800",
      borderColor: "border-orange-200",
      icon: AlertTriangle
    },
    "On Time": {
      bgColor: "bg-green-100",
      textColor: "text-green-800",
      borderColor: "border-green-200",
      icon: Check
    },
    "Delayed": {
      bgColor: "bg-amber-100",
      textColor: "text-amber-800",
      borderColor: "border-amber-200",
      icon: AlertTriangle
    },
    "Departed": {
      bgColor: "bg-teal-100",
      textColor: "text-teal-800",
      borderColor: "border-teal-200",
      icon: Check
    },
    "In Air": {
      bgColor: "bg-sky-100",
      textColor: "text-sky-800",
      borderColor: "border-sky-200",
      icon: Check
    },
    "Diverted": {
      bgColor: "bg-rose-100",
      textColor: "text-rose-800",
      borderColor: "border-rose-200",
      icon: AlertTriangle
    },
    "Recovery": {
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
      borderColor: "border-yellow-200",
      icon: AlertTriangle
    },
    "Landed": {
      bgColor: "bg-emerald-100",
      textColor: "text-emerald-800",
      borderColor: "border-emerald-200",
      icon: Check
    },
    "Arrived": {
      bgColor: "bg-lime-100",
      textColor: "text-lime-800",
      borderColor: "border-lime-200",
      icon: Check
    },
    "Cancelled": {
      bgColor: "bg-red-100",
      textColor: "text-red-800",
      borderColor: "border-red-200",
      icon: X
    }
  };

  const config = statusConfig[status] || {
    bgColor: "bg-slate-100",
    textColor: "text-slate-800",
    borderColor: "border-slate-200",
    icon: Check
  };
  const StatusIcon = config.icon;

  return (
    <div 
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border",
        config.bgColor,
        config.textColor,
        config.borderColor,
        pulsing && status !== "On Time" && "animate-pulse-status",
        className
      )}
    >
      {showIcon && displayStatus !== "-" && (
        <StatusIcon className="mr-1 h-3 w-3" />
      )}
      {displayStatus}
    </div>
  );
}
